import { Box } from '@mui/material'
import { BeforeAfterSlider } from 'components/common/BeforeAfterSlider'
import { PreviewContainer } from './PreviewContainer/PreviewContainer.component'
import React, { useEffect, useState } from 'react'
import Stack from '@mui/material/Stack'
import { VisualTypeSwitcher } from '../../common/VisualTypeSwitcher'
import { useClientPhotoStudio } from './ClientPhotoStudio.context'
import { useLabViewSwitcherProps } from './lab/useLabViewSwitcherProps.hook'
import { useTranslation } from 'react-i18next'
import { useWorkspacePlan } from 'utils/hooks'

/**
 * @component
 * The Adjust tool's preview while the Pintura editor is hidden: the original (ai-visuals engine, and non-subscribers),
 * or — on the Lab SDK engine — the Before of the version on stage, or the Before | After comparison slider.
 *
 * @example
 * <ManualToolOriginalImagePreview />
 */
export const ManualToolOriginalImagePreview: React.FC = () => {
  const { t } = useTranslation(['photo_studio_editing'])

  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()
  const {
    imageUrl,
    previewImage,
    expandedVisualType,
    setExpandedVisualType,
    isOriginalVisualType,
    isLabSdkEngine,
    isLabCompareAvailable,
    isLabCompare,
    labRunSources,
    photoStudioHistory,
  } = useClientPhotoStudio()
  const { canUndo, canRedo } = photoStudioHistory.historyState
  const labSwitcherProps = useLabViewSwitcherProps()

  const isLabCompareView = isLabSdkEngine && isLabCompare
  const shownImage = !isLabSdkEngine ? imageUrl : isOriginalVisualType ? labRunSources.before : previewImage

  // The slider crops to its box (object-fit: cover), so it is sized to the photo's own aspect ratio
  const [aspectRatio, setAspectRatio] = useState<number | undefined>(undefined)
  useEffect(() => {
    if (!isLabCompareView || !previewImage) return
    const image = new Image()
    image.onload = () => {
      if (image.naturalWidth && image.naturalHeight) setAspectRatio(image.naturalWidth / image.naturalHeight)
    }
    image.src = previewImage
  }, [isLabCompareView, previewImage])

  return (
    <PreviewContainer hasWorkspaceActiveSubscription={hasWorkspaceActiveSubscription}>

      <Stack
        zIndex={10}
        height="5.6rem"
        width="100%"
        padding="1rem 0"
        justifyContent="center"
        alignItems="center"
      >
        {!isLabSdkEngine && (
          <VisualTypeSwitcher
            expandedVisualType={expandedVisualType}
            switchExpandedVisualType={setExpandedVisualType}
            disabled={!hasWorkspaceActiveSubscription}
          />
        )}
        {isLabSdkEngine && (canUndo || canRedo) && (
          <VisualTypeSwitcher
            {...labSwitcherProps}
            disabled={!hasWorkspaceActiveSubscription || !isLabCompareAvailable}
          />
        )}
      </Stack>

      <Box
        sx={{
          flex: '1 1 0%',
          minHeight: 0,
          maxHeight: '100%',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingLeft: '1.6rem',
          paddingRight: '1.6rem',
          paddingBottom: '5.6rem',
        }}
      >
        <Stack
          sx={{
            position: 'relative',
            display: 'inline-block',
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            overflow: 'hidden'
          }}
        >
          {isLabCompareView && (
            <Stack sx={{ width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
              <BeforeAfterSlider
                beforeImage={labRunSources.before}
                afterImage={previewImage}
                height="100%"
                handleAriaLabel={t('photo_studio_editing:enhance.before_after_slider_aria_label')}
                sx={{ aspectRatio, width: aspectRatio ? 'auto' : '100%', maxWidth: '100%', maxHeight: '100%', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}
              />
            </Stack>
          )}
          {!isLabCompareView && (
            <img
              src={shownImage}
              alt="loaded_visual_for_manual_tool_original_preview"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                display: 'block',
                margin: 'auto',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                pointerEvents: 'none',
              }}
            />
          )}
        </Stack>
      </Box>

    </PreviewContainer>
  )
}
